import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { MessageCircle, Mail, Copy, Send, CheckCircle2, Phone, Briefcase, Building2, Clock, Paperclip, X as XIcon, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import {
  SITUATIONS,
  SITUATION_ORDER,
  buildMessage,
  buildWaUrl,
  buildMailto,
  normalizePhoneForWa,
} from "@/lib/messageEngine";

const ATTACH_MIME_HINT = ".pdf,.png,.jpg,.jpeg,.webp,.gif,.heic,.doc,.docx,.xls,.xlsx,.csv,.txt,.zip,.mp4,.mov,.mp3,.wav,.m4a";

const fmtBytes = (n) => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const absoluteUrl = (path) => {
  if (!path) return "";
  if (/^https?:/i.test(path)) return path;
  const origin = (typeof window !== "undefined" ? window.location.origin : "").replace(/\/$/, "");
  return `${origin}${path}`;
};

/**
 * Unified Smart Communication Assistant.
 *
 * Same component on every device — uses a Shadcn Sheet that slides from the
 * BOTTOM on mobile and from the RIGHT on desktop, giving us the bottom-sheet
 * UX on phones and the side-panel UX on desktop without two codepaths.
 *
 * Props:
 *   open                 controlled open state
 *   onOpenChange         setter
 *   contact              { name, phone, email, company }
 *   deal                 { id, title, stage, value, last_interaction_at }
 *   client               { id, company_name, contact_person, last_interaction_at }
 *   defaultSituation     key in SITUATIONS (default: follow_up_action)
 *   action               optional pre-fill for follow_up_action
 *   meetingWhen          optional human-readable meeting time
 *   meetingLocation      optional
 *   followupId           if set, completing a send will also POST /followups/{id}/ping
 *   onActivityLogged     callback after activity is logged
 */
export default function SmartMessageComposer({
  open,
  onOpenChange,
  contact,
  deal,
  client,
  defaultSituation = "follow_up_action",
  action,
  meetingWhen,
  meetingLocation,
  event,
  quotation,
  totalText,
  validUntil,
  pdfLink,
  company,
  followupId,
  onActivityLogged,
}) {
  const { user } = useAuth();
  const [situation, setSituation] = useState(defaultSituation);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [lastChannel, setLastChannel] = useState(null);
  const [askConfirm, setAskConfirm] = useState(false);
  const [logging, setLogging] = useState(false);
  const [attachments, setAttachments] = useState([]); // [{id,url_path,original_name,size_bytes,mime_type,_blob?}]
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [linkedQuotations, setLinkedQuotations] = useState([]);
  const [showQuotePicker, setShowQuotePicker] = useState(false);
  const [attachingQuote, setAttachingQuote] = useState(false);

  // Reset attachments when the sheet closes — never carry files between sends.
  useEffect(() => {
    if (!open) {
      setAttachments([]);
      setShowQuotePicker(false);
    }
  }, [open]);

  // Fetch quotations linked to the current deal/client so the user can attach
  // them in one click. Runs whenever the sheet opens with a known context.
  useEffect(() => {
    if (!open) { setLinkedQuotations([]); return; }
    const params = {};
    if (deal?.id) params.deal_id = deal.id;
    else if (client?.id) params.client_id = client.id;
    if (!params.deal_id && !params.client_id) { setLinkedQuotations([]); return; }
    (async () => {
      try {
        const { data } = await api.get("/quotations", { params });
        setLinkedQuotations(Array.isArray(data) ? data : []);
      } catch { setLinkedQuotations([]); }
    })();
  }, [open, deal?.id, client?.id]);

  // Whenever the assistant is opened (or the situation changes), re-render
  // the message from the engine using the freshest context.
  useEffect(() => {
    if (!open) return;
    setSituation(defaultSituation);
  }, [open, defaultSituation]);

  useEffect(() => {
    if (!open) return;
    const ctx = {
      situation,
      contact: {
        name: contact?.name || client?.contact_person || client?.company_name,
        phone: contact?.phone || client?.phone,
        email: contact?.email || client?.email,
        company: client?.company_name || contact?.company,
      },
      sender: user,
      deal: deal ? { title: deal.title, company: client?.company_name } : null,
      action,
      meetingWhen,
      meetingLocation,
      event,
      quotation,
      totalText,
      validUntil,
      pdfLink,
      company,
    };
    const m = buildMessage(ctx);
    setSubject(m.subject || "");
    setBody(m.body || "");
    setLastChannel(null);
    setAskConfirm(false);
  }, [open, situation, contact, client, deal, action, meetingWhen, meetingLocation, event, quotation, totalText, validUntil, pdfLink, company, user]);

  const phone = contact?.phone || client?.phone;
  const email = contact?.email || client?.email;
  const contactName = contact?.name || client?.contact_person || client?.company_name;

  // ── Attachments ──────────────────────────────────────────────────────────
  const handleFilesPicked = async (files) => {
    const list = Array.from(files || []);
    if (list.length === 0) return;
    setUploading(true);
    let success = 0;
    for (const f of list) {
      if (f.size > 25 * 1024 * 1024) {
        toast.error(`${f.name} exceeds 25 MB limit`);
        continue;
      }
      try {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("context", "message");
        const { data } = await api.post("/attachments/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        // Keep the original Blob alongside the server URL so we can use the
        // native Web Share API on mobile to attach the actual file (rather
        // than the link) into WhatsApp / Messages / Mail.
        setAttachments((prev) => [...prev, { ...data, _blob: f }]);
        success += 1;
      } catch (err) {
        toast.error(err?.response?.data?.detail || `Upload failed for ${f.name}`);
      }
    }
    if (success) toast.success(`${success} file${success > 1 ? "s" : ""} attached`);
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeAttachment = (id) => setAttachments((prev) => prev.filter((a) => a.id !== id));

  // Materialise a quotation PDF into the public attachments folder, then add
  // it to the local attachments state. We also fetch the binary so the
  // native share API path can attach the actual file (not just the link).
  const attachQuotationPdf = async (quotationId) => {
    setAttachingQuote(true);
    try {
      const { data: rec } = await api.post(`/quotations/${quotationId}/attach-to-message`);
      let blob = null;
      try {
        const r = await api.get(rec.url_path, { responseType: "blob" });
        blob = new File([r.data], rec.original_name, { type: rec.mime_type });
      } catch { /* link-only fallback */ }
      setAttachments((prev) => [...prev, { ...rec, _blob: blob }]);
      toast.success(`${rec.original_name} attached`);
      setShowQuotePicker(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not attach quotation");
    } finally { setAttachingQuote(false); }
  };

  const handleAttachQuotationClick = () => {
    if (linkedQuotations.length === 0) { toast.error("No quotations on this deal/client yet"); return; }
    if (linkedQuotations.length === 1) { attachQuotationPdf(linkedQuotations[0].id); return; }
    setShowQuotePicker(true);
  };

  // The link-list block we append to message bodies (WhatsApp/Email/Copy).
  const attachmentLinkBlock = useMemo(() => {
    if (attachments.length === 0) return "";
    const lines = attachments.map((a) => `📎 ${a.original_name}: ${absoluteUrl(a.url_path)}`);
    return `\n\nAttachments:\n${lines.join("\n")}`;
  }, [attachments]);

  // True when the browser can natively share files (mobile Safari, Chrome
  // Android, recent desktop Chrome). Used to ALSO offer "Share with files"
  // which attaches the actual files to the WhatsApp/Mail share sheet.
  const canNativeShareFiles = useMemo(() => {
    if (typeof navigator === "undefined") return false;
    if (!navigator.canShare || !navigator.share) return false;
    if (attachments.length === 0) return false;
    try {
      const files = attachments.map((a) => a._blob).filter(Boolean);
      if (files.length === 0) return false;
      return navigator.canShare({ files });
    } catch { return false; }
  }, [attachments]);

  const handleNativeShare = async () => {
    try {
      const files = attachments.map((a) => a._blob).filter(Boolean);
      await navigator.share({
        title: subject || "Message",
        text: body, // exclude link-list since we're attaching files directly
        files,
      });
      setLastChannel("whatsapp");
      setAskConfirm(true);
    } catch (err) {
      // User cancelled OR browser refused — fall back silently.
      if (err?.name !== "AbortError") {
        toast.error("Native share unavailable, try WhatsApp / Email / Copy");
      }
    }
  };

  const handleWhatsApp = () => {
    const url = buildWaUrl(phone, body + attachmentLinkBlock);
    if (!url) { toast.error("No valid phone number on file"); return; }
    window.open(url, "_blank", "noopener,noreferrer");
    setLastChannel("whatsapp");
    setAskConfirm(true);
  };

  const handleEmail = () => {
    if (!email) { toast.error("No email on file"); return; }
    const url = buildMailto(email, subject, body + attachmentLinkBlock);
    if (url) window.location.href = url;
    setLastChannel("email");
    setAskConfirm(true);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(body + attachmentLinkBlock);
      toast.success(attachments.length ? "Message + links copied" : "Message copied");
      setLastChannel("copy");
      setAskConfirm(true);
    } catch (e) {
      toast.error("Could not copy. Long-press to copy manually.");
    }
  };

  const logActivity = async (markCompleted) => {
    if (!markCompleted) {
      onOpenChange(false);
      return;
    }
    setLogging(true);
    try {
      const channelLabel = lastChannel === "whatsapp" ? "WhatsApp" : lastChannel === "email" ? "Email" : "Copy";
      const attachLabel = attachments.length ? ` with ${attachments.length} attachment${attachments.length > 1 ? "s" : ""}` : "";
      const summary = `${SITUATIONS[situation]?.label || "Message"} sent via ${channelLabel}${attachLabel}`;
      await api.post("/activities", {
        type: "message",
        deal_id: deal?.id || null,
        client_id: client?.id || deal?.client_id || null,
        summary,
        details: body + (attachments.length ? `\n\nAttachments:\n${attachments.map((a) => `${a.original_name} (${absoluteUrl(a.url_path)})`).join("\n")}` : ""),
        channel: lastChannel,
        message_type: situation,
        preview: body.slice(0, 280),
        attachments: attachments.map((a) => ({
          id: a.id,
          original_name: a.original_name,
          url_path: a.url_path,
          size_bytes: a.size_bytes,
          mime_type: a.mime_type,
        })),
      });
      // If this was launched from a follow-up, also soft-mark "pinged"
      if (followupId) {
        try {
          const via = lastChannel === "email" ? "email" : lastChannel === "whatsapp" ? "whatsapp" : "whatsapp";
          await api.post(`/followups/${followupId}/ping`, { via });
        } catch (e) { /* silent */ }
      }
      toast.success("Logged in activity timeline");
      onActivityLogged && onActivityLogged();
      onOpenChange(false);
    } catch (err) {
      toast.error("Could not log activity");
    } finally { setLogging(false); }
  };

  const stageLabel = (deal?.stage || "").replace(/_/g, " ");

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-xl rounded-t-3xl sm:rounded-l-3xl sm:rounded-tr-none p-0 overflow-y-auto data-[mobile=true]:rounded-l-none"
        data-testid="smart-composer"
      >
        <div className="flex flex-col min-h-full">
          <SheetHeader
            className="px-5 pb-3 border-b border-border/60 sticky top-0 bg-background z-10"
            style={{ paddingTop: "calc(env(safe-area-inset-top) + 16px)" }}
          >
            <SheetTitle className="font-display text-2xl font-extrabold flex items-center gap-2">
              <MessageCircle className="h-5 w-5 text-primary" /> Send message
            </SheetTitle>
            <SheetDescription className="text-xs text-muted-foreground">
              Pick the situation, edit if needed, then choose how to send. Always formal tone.
            </SheetDescription>
          </SheetHeader>

          {/* CONTEXT (visible on desktop, collapses on mobile) */}
          {(client || deal || contactName) && (
            <div className="px-5 py-3 bg-muted/30 border-b border-border/60 text-xs space-y-1.5" data-testid="composer-context">
              {client?.company_name && (
                <p className="inline-flex items-center gap-1.5"><Building2 className="h-3 w-3 text-muted-foreground" /><span className="font-semibold">{client.company_name}</span>{contactName && contactName !== client.company_name ? <span className="text-muted-foreground">· {contactName}</span> : null}</p>
              )}
              {deal?.title && (
                <p className="inline-flex items-center gap-1.5"><Briefcase className="h-3 w-3 text-muted-foreground" />{deal.title}{stageLabel ? <Badge variant="outline" className="text-[10px] uppercase tracking-wider ml-1.5 capitalize">{stageLabel}</Badge> : null}</p>
              )}
              <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-muted-foreground pt-0.5">
                {phone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" />{phone}</span>}
                {email && <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" />{email}</span>}
                {(client?.last_interaction_at || deal?.last_interaction_at) && (
                  <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />Last contact {new Date(client?.last_interaction_at || deal?.last_interaction_at).toLocaleDateString("en-IN")}</span>
                )}
              </div>
            </div>
          )}

          {/* SITUATIONS as large pill chips (mobile-friendly tap targets) */}
          <div className="px-5 pt-4">
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-2">Situation</p>
            <div className="flex flex-wrap gap-2">
              {SITUATION_ORDER.map((k) => {
                const s = SITUATIONS[k];
                if (!s) return null;
                const active = situation === k;
                return (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setSituation(k)}
                    data-testid={`composer-situation-${k}`}
                    className={`px-3 py-2 text-xs font-semibold rounded-2xl border tap transition ${
                      active
                        ? "bg-primary text-primary-foreground border-primary shadow-soft"
                        : "bg-card text-foreground border-border/60 hover:border-primary/60"
                    }`}
                  >
                    {s.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* SUBJECT (only relevant for email but always editable) */}
          <div className="px-5 pt-4">
            <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">Subject (email)</Label>
            <Input
              className="rounded-2xl h-11 mt-1.5 text-sm"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              data-testid="composer-subject-input"
            />
          </div>

          {/* BODY */}
          <div className="px-5 pt-4 flex-1">
            <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">Message</Label>
            <Textarea
              rows={8}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              className="rounded-2xl mt-1.5 font-mono text-[12.5px] leading-relaxed max-h-[40vh]"
              data-testid="composer-body-textarea"
            />
            <p className="text-[10px] text-muted-foreground mt-1.5">{body.length} characters</p>
          </div>

          {/* ATTACHMENTS */}
          <div className="px-5 pt-4">
            <div className="flex items-center justify-between flex-wrap gap-1.5">
              <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">
                Attachments
              </Label>
              <div className="flex items-center gap-1">
                {linkedQuotations.length > 0 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleAttachQuotationClick}
                    disabled={attachingQuote || uploading}
                    className="rounded-2xl tap text-xs text-emerald-700 hover:bg-emerald-50"
                    data-testid="composer-attach-quote-button"
                    title={linkedQuotations.length === 1 ? `Attach ${linkedQuotations[0].number}` : `Pick from ${linkedQuotations.length} quotations`}
                  >
                    {attachingQuote ? (
                      <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Attaching…</>
                    ) : (
                      <><FileText className="h-3.5 w-3.5 mr-1" /> {linkedQuotations.length === 1 ? "Attach quote" : `Attach quote (${linkedQuotations.length})`}</>
                    )}
                  </Button>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept={ATTACH_MIME_HINT}
                  className="hidden"
                  onChange={(e) => handleFilesPicked(e.target.files)}
                  data-testid="composer-file-input"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="rounded-2xl tap text-xs text-emerald-700 hover:bg-emerald-50"
                  data-testid="composer-attach-button"
                >
                  {uploading ? (
                    <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Uploading…</>
                  ) : (
                    <><Paperclip className="h-3.5 w-3.5 mr-1" /> Add files</>
                  )}
                </Button>
              </div>
            </div>

            {showQuotePicker && linkedQuotations.length > 1 && (
              <div className="mt-2 p-2 rounded-2xl border border-emerald-200 bg-emerald-50/50" data-testid="composer-quote-picker">
                <div className="flex items-center justify-between mb-1.5 px-1">
                  <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-700">Pick a quotation</p>
                  <button type="button" onClick={() => setShowQuotePicker(false)} className="text-emerald-700/70 hover:text-emerald-900 tap">
                    <XIcon className="h-3 w-3" />
                  </button>
                </div>
                <div className="space-y-1 max-h-44 overflow-y-auto">
                  {linkedQuotations.map((q) => (
                    <button
                      key={q.id}
                      type="button"
                      onClick={() => attachQuotationPdf(q.id)}
                      disabled={attachingQuote}
                      className="w-full flex items-center gap-2 px-3 py-2 rounded-2xl bg-white border border-emerald-100 hover:bg-emerald-50 text-left tap text-xs"
                      data-testid={`composer-quote-pick-${q.id}`}
                    >
                      <FileText className="h-3.5 w-3.5 text-emerald-700 shrink-0" />
                      <span className="flex-1 min-w-0 font-semibold truncate">{q.number || "Untitled"}</span>
                      <span className="text-muted-foreground capitalize">{(q.status || "").replace(/_/g, " ")}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {attachments.length === 0 ? (
              <p className="text-[11px] text-muted-foreground mt-1.5">
                Attach PDFs, images, docs (max 25 MB each). Links are appended to the message; on mobile we'll attach the actual files when supported.
              </p>
            ) : (
              <div className="mt-2 space-y-1.5" data-testid="composer-attachments-list">
                {attachments.map((a) => (
                  <div
                    key={a.id}
                    className="flex items-center gap-2 px-3 py-2 rounded-2xl border border-border/60 bg-muted/30"
                    data-testid={`composer-attachment-${a.id}`}
                  >
                    <FileText className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium truncate">{a.original_name}</p>
                      <p className="text-[10px] text-muted-foreground">{fmtBytes(a.size_bytes)}{a.context === "quotation" ? " · Quotation PDF" : ""}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeAttachment(a.id)}
                      className="p-1 rounded-full hover:bg-red-50 tap text-red-600 shrink-0"
                      aria-label={`Remove ${a.original_name}`}
                      data-testid={`composer-attachment-remove-${a.id}`}
                    >
                      <XIcon className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* SEND ACTIONS */}
          <div className="px-5 pt-3 pb-5 border-t border-border/60 mt-3 bg-card/50 backdrop-blur-sm sticky bottom-0">
            {!askConfirm ? (
              <div className="space-y-2">
                {canNativeShareFiles && (
                  <Button
                    type="button"
                    onClick={handleNativeShare}
                    className="rounded-2xl h-12 w-full tap bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="composer-native-share-button"
                  >
                    <Send className="h-4 w-4 mr-1.5" /> Share with attached files
                  </Button>
                )}
                <div className="grid grid-cols-3 gap-2">
                  <Button
                    type="button"
                    onClick={handleWhatsApp}
                    disabled={!phone || !normalizePhoneForWa(phone)}
                    className="rounded-2xl h-12 tap bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="composer-whatsapp-button"
                  >
                    <MessageCircle className="h-4 w-4 mr-1.5" /> WhatsApp
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleEmail}
                    disabled={!email}
                    className="rounded-2xl h-12 tap"
                    data-testid="composer-email-button"
                  >
                    <Mail className="h-4 w-4 mr-1.5" /> Email
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCopy}
                    className="rounded-2xl h-12 tap"
                    data-testid="composer-copy-button"
                  >
                    <Copy className="h-4 w-4 mr-1.5" /> Copy
                  </Button>
                </div>
                {attachments.length > 0 && !canNativeShareFiles && (
                  <p className="text-[10px] text-muted-foreground text-center">
                    {attachments.length} file{attachments.length > 1 ? "s" : ""} will be sent as download links
                    (WhatsApp Web doesn't support direct file attachment).
                  </p>
                )}
              </div>
            ) : (
              <div data-testid="composer-confirm">
                <p className="text-sm font-semibold mb-3 flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-primary" /> Mark this communication as completed?</p>
                <p className="text-xs text-muted-foreground mb-3">
                  Saves to the activity timeline · channel: <strong className="capitalize">{lastChannel}</strong>{followupId ? " · also pings the follow-up" : ""}
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <Button variant="outline" className="rounded-2xl h-11 tap" onClick={() => logActivity(false)} data-testid="composer-skip-log">
                    Skip
                  </Button>
                  <Button onClick={() => logActivity(true)} disabled={logging} className="rounded-2xl h-11 tap" data-testid="composer-confirm-log">
                    <Send className="h-4 w-4 mr-1.5" /> {logging ? "Saving…" : "Yes, log it"}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
